import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageContainer from "@/components/layouts/PageContainer";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { PlusIcon } from "@heroicons/react/20/solid";
import { Button } from "@/components/button";
import { PaginationComponent } from "@/components/PaginationComponent";
import {
  useQueryGetExamConfigList,
  type IExamConfigListParams,
} from "@/apis/exam_configs/getExamConfigList";
import { getPaginationInfo } from "@/types/pagination";
import ExamConfigListTable from "./ExamConfigListTable";

const ExamConfigListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useState<IExamConfigListParams>({
    page: 1,
    limit: 10,
  });

  // API 호출
  const { data, isLoading, error } = useQueryGetExamConfigList(searchParams);

  // 페이지네이션 정보
  const paginationInfo = data ? getPaginationInfo(data) : null;

  // 페이지 이동 핸들러
  const handlePageChange = (page: number) => {
    setSearchParams((prev) => ({ ...prev, page }));
  };

  // 설정 추가 페이지로 이동
  const handleAddConfiguration = () => {
    navigate("/exam_configs/create");
  };

  return (
    <PageContainer>
      <div className="space-y-6">
        <Heading>Exam Configurations</Heading>
        <Text className="text-zinc-500">
          View all exam configurations available in the system. Each
          configuration defines the subjects, duration, and calculator
          permissions for different exam types.
        </Text>

        <div className="flex justify-between mt-4">
          <div className="flex items-center gap-3">
            <Button onClick={handleAddConfiguration}>
              <PlusIcon />
              Add Configuration
            </Button>
          </div>
        </div>

        {/* Exam Config List Table Component */}
        <ExamConfigListTable
          examConfigs={data?.items || []}
          isLoading={isLoading}
          error={error as Error | null}
        />

        {/* Pagination */}
        {paginationInfo && (
          <div className="mt-8 flex justify-center">
            <PaginationComponent
              currentPage={paginationInfo.currentPage}
              totalPages={paginationInfo.totalPages}
              hasNextPage={paginationInfo.hasNextPage}
              hasPrevPage={paginationInfo.hasPrevPage}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </PageContainer>
  );
};

export default ExamConfigListPage;
